import { Skeleton } from "@/components/ui/skeleton";
import { GRID_GAP, ROW_HEIGHT, SIZE_SPANS } from "../grid.constants";
import { scaleSpan } from "../grid.utils";
import type { IWidgetPlacement } from "../types";

interface CanvasSkeletonProps {
  columns: number;
  /** The arrangement about to render, so the placeholders land where the cards will. */
  layout: IWidgetPlacement[];
}

/**
 * Placeholder for the canvas while the saved layout and the permission set
 * resolve.
 *
 * Shaped from the default arrangement rather than a generic row of bars, so the
 * canvas loads at roughly the right rhythm instead of a stack of grey lines.
 *
 * Only *roughly*: the saved layout is what is being fetched, so at first paint
 * there is nothing better to draw. A user who has customised heavily will still
 * see one re-flow when their own arrangement arrives. Reading the last known
 * shape back from local storage would close that, at the cost of a second place
 * layouts live — not worth it until someone complains about the flash.
 */
export const CanvasSkeleton = ({ columns, layout }: CanvasSkeletonProps) => (
  <div
    aria-hidden
    className="grid"
    style={{
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      gridAutoRows: `${ROW_HEIGHT}px`,
      gridAutoFlow: "row dense",
      gap: GRID_GAP,
    }}
  >
    {layout.map((placement, index) => {
      const span = scaleSpan(SIZE_SPANS[placement.size], columns);
      return (
        <div
          key={placement.instanceId}
          style={{
            gridColumn: `span ${span.w}`,
            gridRow: `span ${span.h}`,
          }}
          className="widget-card flex flex-col gap-2 p-4"
        >
          <div className="flex items-center gap-2">
            <Skeleton className="size-7 shrink-0 rounded-[9px]" soft />
            <Skeleton
              className="h-2.5 w-24 rounded-full"
              soft
              style={{ ["--shimmer-delay" as string]: `${index * 90}ms` }}
            />
          </div>
          <Skeleton className="h-8 w-20 rounded-lg" soft />
          {span.h > 1 && (
            <Skeleton className="mt-1 h-full w-full rounded" soft />
          )}
        </div>
      );
    })}
  </div>
);
